import React, { useState } from "react";
import { FiMapPin, FiNavigation, FiX } from "react-icons/fi";
import { BsPatchCheckFill } from "react-icons/bs";
import BottomSheet from "../../shared/components/ui/BottomSheet";
import { clearGpsLocation, updateGpsLocation } from "../../api/locationApi";
import { getApiErrorMessage } from "../../api/getApiErrorMessage";
import { getCityMapCenter } from "../../shared/data/locationTaxonomy";
import { reverseGeocode } from "../../shared/services/geocodingService";
import { isPreviewAuthEnabled } from "../auth/previewAuth";
import { getProfileLocationLabel } from "../../shared/utils/profileLocation";
import "./ProfileLocationSheet.css";

function pickCityFromAddress(address = {}) {
  return address.city
    || address.town
    || address.village
    || address.municipality
    || address.suburb
    || address.county
    || "";
}

async function resolvePlaceFromCoordinates(latitude, longitude) {
  const result = await reverseGeocode(latitude, longitude);
  const address = result?.address || {};
  return {
    city: pickCityFromAddress(address),
    country: address.country || "",
  };
}

function readCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location is not supported on this device."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(new Error("Location permission was denied."));
          return;
        }
        reject(new Error("Could not get your current location. Please try again."));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  });
}

function resolveHomeLocation(profile) {
  const home = profile?.settings?.homeLocation;
  if (home && (home.city || home.country)) {
    return {
      city: home.city || profile?.city || "",
      country: home.country || profile?.country || "",
    };
  }

  return {
    city: profile?.city || "",
    country: profile?.country || "",
  };
}

export default function ProfileLocationSheet({
  open,
  onClose,
  profile,
  onUpdated,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const locationLabel = getProfileLocationLabel(profile);
  const isGps = profile?.locationSource === "gps";
  const profileLocationLabel = (() => {
    const home = resolveHomeLocation(profile);
    if (home.city && home.country) return `${home.city}, ${home.country}`;
    return home.city || home.country || "your profile city";
  })();

  const handleUseCurrentLocation = async () => {
    setError("");
    setIsSaving(true);

    try {
      const coords = await readCurrentPosition();

      if (isPreviewAuthEnabled()) {
        const place = await resolvePlaceFromCoordinates(coords.latitude, coords.longitude);
        const homeLocation = profile.settings?.homeLocation || {
          city: profile.city,
          country: profile.country,
        };

        onUpdated?.({
          ...profile,
          latitude: coords.latitude,
          longitude: coords.longitude,
          locationSource: "gps",
          city: place.city || profile.city,
          country: place.country || profile.country,
          settings: {
            ...(profile.settings || {}),
            homeLocation,
          },
        });
        onClose?.();
        return;
      }

      const result = await updateGpsLocation(coords.latitude, coords.longitude);
      onUpdated?.(result.profile);
      onClose?.();
    } catch (err) {
      setError(getApiErrorMessage(err, err.message || "Could not update your location."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearGpsLocation = async () => {
    if (!isGps || isSaving) return;

    setError("");
    setIsSaving(true);

    try {
      if (isPreviewAuthEnabled()) {
        const home = resolveHomeLocation(profile);
        const cityCenter = getCityMapCenter(home.city, home.country);

        onUpdated?.({
          ...profile,
          locationSource: "city",
          city: home.city,
          country: home.country,
          latitude: cityCenter?.lat ?? profile.latitude,
          longitude: cityCenter?.lng ?? profile.longitude,
        });
        onClose?.();
        return;
      }

      const result = await clearGpsLocation();
      onUpdated?.(result.profile);
      onClose?.();
    } catch (err) {
      setError(getApiErrorMessage(err, err.message || "Could not turn off live location."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Your location"
      className="profile-location-sheet"
    >
      <div className="profile-location-sheet__body">
        <div className="profile-location-sheet__current">
          <FiMapPin aria-hidden />
          <div>
            <p className="profile-location-sheet__label">{locationLabel}</p>
            <p className="profile-location-sheet__hint">
              {isGps
                ? "Your live GPS position is shown on the map."
                : "Your profile city gives an approximate location on the map."}
            </p>
          </div>
        </div>

        {isGps ? (
          <button
            type="button"
            className="profile-location-sheet__status profile-location-sheet__status--gps profile-location-sheet__status--action"
            onClick={handleClearGpsLocation}
            disabled={isSaving}
            aria-label="Turn off live location and use profile city"
          >
            <BsPatchCheckFill aria-hidden />
            <span className="profile-location-sheet__status-copy">
              <span className="profile-location-sheet__status-title">Live location on</span>
              <span className="profile-location-sheet__status-sub">
                Tap to use {profileLocationLabel}
              </span>
            </span>
            <FiX className="profile-location-sheet__status-dismiss" aria-hidden />
          </button>
        ) : (
          <div className="profile-location-sheet__status">
            <FiMapPin aria-hidden />
            <span className="profile-location-sheet__status-copy">
              <span className="profile-location-sheet__status-title">Profile city location</span>
              <span className="profile-location-sheet__status-sub">Approximate map position</span>
            </span>
          </div>
        )}

        <p className="profile-location-sheet__copy">
          Sharing your current location is optional. People nearby will see a green live pin
          when you choose to share your real GPS position.
        </p>

        {error ? (
          <p className="profile-location-sheet__error" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          className="profile-location-sheet__action"
          onClick={handleUseCurrentLocation}
          disabled={isSaving}
        >
          <FiNavigation aria-hidden />
          {isSaving ? "Updating location..." : "Use Current Location"}
        </button>
      </div>
    </BottomSheet>
  );
}
